import { CustomerTagProvider } from "@redotech/redo-common-model-tags-common/customer-tag-record";
import { Provider } from "@redotech/redo-order-common/provider";

export interface ProviderCapabilities {
  /** Whether discount code generation UI should be shown. CS has no discount API. */
  supportsDiscountCodes: boolean;
  /**
   * Whether the provider supports a multi-item cart URL.
   * Shopify: /cart/VARIANT:QTY,... CS: redirects to the first product's PDP instead.
   */
  supportsCartUrl: boolean;
  /**
   * Whether to fall back to the store domain when no products are found.
   * CS returns null instead — team.storeUrl is the Shopify domain, not the CS storefront.
   */
  fallbackToStoreDomain: boolean;
  /** The provider's native customer-tag value; REDO when it has none. */
  customerTagProvider: CustomerTagProvider;

  supportsProductRecommendations: boolean;
}

// Only providers that differ from the defaults need explicit entries; all
// others fall back via getProviderCapabilities().
const PROVIDER_CAPABILITIES_MAP: Partial<
  Record<Provider, ProviderCapabilities>
> = {
  [Provider.SHOPIFY]: {
    supportsDiscountCodes: true,
    supportsCartUrl: true,
    fallbackToStoreDomain: true,
    customerTagProvider: CustomerTagProvider.SHOPIFY,
    supportsProductRecommendations: true,
  },
  [Provider.COMMENTSOLD]: {
    supportsDiscountCodes: false,
    supportsCartUrl: false,
    fallbackToStoreDomain: false,
    customerTagProvider: CustomerTagProvider.COMMENTSOLD,
    supportsProductRecommendations: true,
  },
  [Provider.BIGCOMMERCE]: {
    supportsDiscountCodes: true,
    supportsCartUrl: true,
    fallbackToStoreDomain: true,
    customerTagProvider: CustomerTagProvider.REDO,
    supportsProductRecommendations: true,
  },
  [Provider.COMMERCE_CLOUD]: {
    supportsDiscountCodes: true,
    supportsCartUrl: false,
    fallbackToStoreDomain: true,
    customerTagProvider: CustomerTagProvider.REDO,
    supportsProductRecommendations: true,
  },
  [Provider.REDO]: {
    supportsDiscountCodes: false,
    supportsCartUrl: false,
    fallbackToStoreDomain: false,
    customerTagProvider: CustomerTagProvider.REDO,
    supportsProductRecommendations: false,
  },
  [Provider.REDO_API]: {
    supportsDiscountCodes: false,
    supportsCartUrl: false,
    fallbackToStoreDomain: false,
    customerTagProvider: CustomerTagProvider.REDO,
    supportsProductRecommendations: false,
  },
  // Custom-integration order providers (custom return integration, NetSuite,
  // Aptos) resolve to providers with no catalog, discount, or cart API.
  [Provider.CUSTOM_RETURN_INTEGRATION]: {
    supportsDiscountCodes: false,
    supportsCartUrl: false,
    fallbackToStoreDomain: false,
    customerTagProvider: CustomerTagProvider.REDO,
    supportsProductRecommendations: false,
  },
  [Provider.NETSUITE]: {
    supportsDiscountCodes: false,
    supportsCartUrl: false,
    fallbackToStoreDomain: false,
    customerTagProvider: CustomerTagProvider.REDO,
    supportsProductRecommendations: false,
  },
  [Provider.APTOS]: {
    supportsDiscountCodes: false,
    supportsCartUrl: false,
    fallbackToStoreDomain: false,
    customerTagProvider: CustomerTagProvider.REDO,
    supportsProductRecommendations: false,
  },
};

const DEFAULT_PROVIDER_CAPABILITIES: ProviderCapabilities = {
  supportsDiscountCodes: true,
  supportsCartUrl: false,
  fallbackToStoreDomain: true,
  customerTagProvider: CustomerTagProvider.REDO,
  supportsProductRecommendations: true,
};

/** SMS capabilities for a provider. Unlisted providers do not get Shopify cart permalinks. */
export function getProviderCapabilities(
  provider: Provider,
): ProviderCapabilities {
  return PROVIDER_CAPABILITIES_MAP[provider] ?? DEFAULT_PROVIDER_CAPABILITIES;
}
